import { ReactNode, Ref, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AccountBalance,
  OrganizationEntity,
  PartnerOnboardingEntityRequest,
  PartnerOrganization,
  Skeleton,
  SubPartnerAccount,
  countryToISO,
  withStyles,
} from 'vopay';
import { useBlockingLoader } from 'shared';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useToast } from '@/providers/Toast.provider';
import useStorage from '@/hooks/useStorage';
import useFetchPartnerPartners from '@/hooks/useApi/useFetchPartnerPartners';
import useFetchPartnerAccounts from '@/components/Entities/hooks/useApi/useFetchPartnerAccounts';
import { findPartnerToken } from '@/components/Entities/hooks/useApi/usePartnerBearer';
import AccountDetailsDrawer from '@/components/Entities/Accounts/AccountDetailsDrawer/AccountDetailsDrawer';
import PartnerAccountCreation, {
  LockedParentAccount,
  ScopedPartnerAccount,
} from '@/components/Entities/Accounts/AccountCreation/PartnerAccountCreation/PartnerAccountCreation';
import * as accountPageRoute from '@/components/Entities/Accounts/accountPage.route';
import * as accountCreationRoute from '@/components/Entities/Accounts/AccountCreation/accountCreation.route';
import ApplicationDetailsDrawer from '@/components/OnboardingTracker/ApplicationDetailsDrawer/ApplicationDetailsDrawer';
import PartnerCreationDrawer from '@/components/Partners/PartnerManagement/PartnerCreationDrawer';
import PartnerDetailsDrawer from '@/components/Partners/PartnerManagement/PartnerDetailsDrawer/PartnerDetailsDrawer';
import * as partnerPageRoute from '@/components/Partners/PartnerManagement/partnerPage.route';
import canCreatePartners from '@/components/Partners/PartnerManagement/canCreatePartners.helper';
import OrganizationChart from './OrganizationChart';
import OrganizationChartControls from './OrganizationChartControls';
import type { EntityCreation, EntityCreationPermissions } from './entityCreationsFor.helper';
import { OrganizationFilter, OrganizationNode, OrganizationShowFilter } from './organizationTree';
import styles from './OrganizationChartPanel.module.scss';

const cn = withStyles(styles);

/** What a host's own header buttons open: the same creation drawers the cards do. */
export type OrganizationChartPanelHandle = {
  createPartner: () => void;
  createAccount: () => void;
};

type OrganizationChartPanelProps = {
  ref?: Ref<OrganizationChartPanelHandle>;
  /** Undefined until the first load has answered, or when it failed. */
  organization: PartnerOrganization | undefined;
  /** The first load only: a refetch keeps the chart up and swaps the data in underneath. */
  isLoading: boolean;
  show: OrganizationShowFilter;
  filter: OrganizationFilter;
  /** The controls row's right-hand side: the Overview's Show and Status filters. */
  filters?: ReactNode;
  /** Set when the panel sits inside a page's card rather than being one: it draws its own edge. */
  isInset?: boolean;
  /** A card created an entity, or Track Progress changed one: the host refetches what the chart draws. */
  onOrganizationChanged: () => void;
};

/** The entity a card's Track Progress names, in the onboarding endpoints' vocabulary. */
function toOnboardingEntity({ EntityType, EntityID }: OrganizationEntity): PartnerOnboardingEntityRequest {
  return { EntityType: EntityType === 'Sub-Account' ? 'SubAccount' : EntityType, EntityID };
}

/**
 * The chart and everything a card leads to — the Display Levels / Show Up To row, the canvas, the
 * details drawers a click opens, the creation drawers a hover offers (spec 6.6) and Track Progress
 * (6.8). One component behind the Organization Overview and the Accounts and Partners chart views
 * (ID-14095); the host owns the data and the filters.
 */
export default function OrganizationChartPanel({
  ref,
  organization,
  isLoading,
  show,
  filter,
  filters,
  isInset,
  onOrganizationChanged,
}: OrganizationChartPanelProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Entities.OrganizationOverviewPage' });
  const history = useHistory();
  const toast = useToast();
  const blockingLoader = useBlockingLoader();

  const { permissions } = usePermissions();

  const {
    login: { getPartnerLogin },
  } = useStorage();

  const { canCreateAccount } = useModuleFeatureFlag('accounts');

  const {
    response: { isRoleAdmin, isRoleViewOnly, isInsideSales, partnerType },
  } = usePartnerData();

  // The Partner Accounts page's own rule for its Add Partner button.
  const canCreatePartner = canCreatePartners({
    isRoleAdmin,
    isPartnerCreationEnabled: permissions.PartnerCreationEnabled,
    partnerType,
  });

  // Partner Details reads /partner/partners, which the backend gates on the permission alone, so any
  // role may open it; only its link to the partner page is kept to the users those routes admit.
  const canOpenPartnerDetails = permissions.PartnerCreationEnabled;
  // Track Progress opens the Application Details drawer, whose endpoints refuse a partner without
  // AccountOnboardingFormAccess and a Support user; the Onboarding Tracker's routes draw the same line.
  const canTrackProgress = permissions.accountOnboardingFormAccess && (isRoleAdmin || isRoleViewOnly);

  // What each card may offer on hover (spec 6.6). The per-entity half of each rule — the partner
  // Active, the account active with sub-accounts enabled — is the chart's.
  const creationPermissions = useMemo<EntityCreationPermissions>(
    () => ({
      canAddPartnerToOrganization: canCreatePartner,
      // Creating from a card is an Admin's action, whatever the Accounts page itself offers a Support user.
      canAddAccountToOrganization: isRoleAdmin && canCreateAccount,
      // Acting as a child partner (its token signs the creation) is Admin-only, as every other way
      // into a child partner is; Inside Sales creates through its own wizard, which cannot act as one.
      canAddAccountUnderPartner: isRoleAdmin && canCreateAccount && !isInsideSales,
      // The Sub-Accounts tab's rule: an Admin, and not an Inside Sales partner.
      canAddSubAccountUnderAccount: isRoleAdmin && !isInsideSales,
    }),
    [canCreatePartner, canCreateAccount, isInsideSales, isRoleAdmin],
  );

  const { fetchPartnerAccounts } = useFetchPartnerAccounts();
  const { fetchPartnerPartners } = useFetchPartnerPartners();

  const [displayLevel, setDisplayLevel] = useState(2);
  const [pageSize, setPageSize] = useState(10);

  const [isCreatePartnerOpen, setIsCreatePartnerOpen] = useState(false);
  const [isCreateAccountOpen, setIsCreateAccountOpen] = useState(false);
  // The child partner an account is being created under (a partner card's + Account).
  const [creationPartner, setCreationPartner] = useState<ScopedPartnerAccount | null>(null);
  // The account a sub-account is being created under (an account card's + Sub-Account).
  const [creationParent, setCreationParent] = useState<LockedParentAccount | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<AccountBalance | null>(null);
  const [isFetchingAccount, setIsFetchingAccount] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<SubPartnerAccount | null>(null);
  const [isFetchingPartner, setIsFetchingPartner] = useState(false);
  const [trackedEntity, setTrackedEntity] = useState<PartnerOnboardingEntityRequest | null>(null);

  // Only the newest lookup may open a drawer: a second card clicked while the first still loads, or
  // the drawer dismissed meanwhile, makes the earlier result stale.
  const accountLookup = useRef(0);
  const partnerLookup = useRef(0);

  // The organization row carries less than the drawer shows, so the account is fetched on click, as
  // the list fetches it for a page opened from a link.
  async function openAccount(entity: OrganizationEntity) {
    accountLookup.current += 1;
    const lookup = accountLookup.current;
    const isLatest = () => accountLookup.current === lookup;

    setSelectedAccount(null);
    setIsFetchingAccount(true);

    try {
      const account = await lookupAccount(entity);

      if (isLatest()) setSelectedAccount(account);
    } catch (error) {
      if (!isLatest()) return;

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('accountLoadError', { defaultValue: 'Could not open the account: {{errorMessage}}', errorMessage }),
      );
    } finally {
      if (isLatest()) setIsFetchingAccount(false);
    }
  }

  // Dismissing the drawer while its account still loads drops that result when it lands.
  function closeAccount() {
    accountLookup.current += 1;
    setSelectedAccount(null);
    setIsFetchingAccount(false);
  }

  /** The account's listing row, which carries what the organization row does not (name, country, token). */
  async function lookupAccount(entity: OrganizationEntity) {
    const { balances } = await fetchPartnerAccounts({ VopayAccountID: entity.EntityID, Limit: 1 });
    const account = balances.find((candidate) => candidate.AccountID === entity.EntityID);

    if (!account) throw new Error(t('accountNotFound', { defaultValue: 'The account is no longer in your book.' }));

    return account;
  }

  // Partner Details needs the partner's list row, which the organization row is not, so it is looked
  // up first (as the Onboarding Tracker does). The drawer opens on placeholders meanwhile.
  async function openPartner(entity: OrganizationEntity) {
    partnerLookup.current += 1;
    const lookup = partnerLookup.current;
    const isLatest = () => partnerLookup.current === lookup;

    setSelectedPartner(null);
    setIsFetchingPartner(true);

    try {
      const partner = await lookupPartner(entity);

      if (isLatest()) setSelectedPartner(partner);
    } catch (error) {
      if (!isLatest()) return;

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('partnerLoadError', { defaultValue: 'Could not open the partner: {{errorMessage}}', errorMessage }),
      );
    } finally {
      if (isLatest()) setIsFetchingPartner(false);
    }
  }

  function closePartner() {
    partnerLookup.current += 1;
    setSelectedPartner(null);
    setIsFetchingPartner(false);
  }

  /** The partner's list row, which Partner Details renders. */
  async function lookupPartner(entity: OrganizationEntity) {
    const { Partners } = await fetchPartnerPartners({ SearchText: entity.EntityID });

    const partner = Partners.find((candidate) => candidate.PartnerAccountID === entity.EntityID);

    if (!partner) throw new Error(t('partnerNotFound', { defaultValue: 'The partner is no longer in your book.' }));

    return partner;
  }

  function openCreateAccount() {
    if (isInsideSales) {
      history.push(accountCreationRoute.getPath());
    } else {
      setIsCreateAccountOpen(true);
    }
  }

  // A host's header opens the same drawers the cards do, so a creation is one drawer and one refetch
  // wherever it starts.
  useImperativeHandle(ref, () => ({
    createPartner: () => setIsCreatePartnerOpen(true),
    createAccount: openCreateAccount,
  }));

  /**
   * + Account on a partner card: the drawer signs every call with the child partner's JWT, so the
   * account is created under — and by the rules of — that partner. The row carries its type; its
   * token comes from the same cached lookup the partner pages use, and the user waits on it, so the
   * loader blocks.
   */
  async function openCreateAccountUnderPartner(entity: OrganizationEntity) {
    blockingLoader.show();

    try {
      // Null only on an account row; a partner card never offers + Account without one.
      if (entity.PartnerType === null) {
        throw new Error(t('partnerTypeUnavailable', { defaultValue: "The partner's type is not available." }));
      }

      const bearer = await findPartnerToken(getPartnerLogin().accountId, entity.EntityID);

      if (!bearer) {
        throw new Error(t('partnerTokenUnavailable', { defaultValue: "The partner's credentials are not available." }));
      }

      setCreationPartner({ bearer, name: entity.Name, partnerType: entity.PartnerType });
    } catch (error) {
      // The token lookup's cause is in its message.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('partnerCreationLoadError', {
          defaultValue: 'Could not prepare the account creation for this partner: {{errorMessage}}',
          errorMessage,
        }),
      );
    } finally {
      blockingLoader.hide();
    }
  }

  /**
   * + Sub-Account on an account card: the drawer locks the parent to this account. The row carries
   * the country the drawer keys the US business-phone requirement on (as the listing names it; the
   * drawer reads alpha-2) and the currency the sub-account inherits.
   */
  function openCreateSubAccount(entity: OrganizationEntity) {
    setCreationParent({
      accountID: entity.EntityID,
      accountName: entity.Name,
      country: countryToISO(entity.Country ?? ''),
      currency: entity.Currency,
    });
  }

  // A card's create button: the card is the parent (spec 6.6), so no parent is picked in the drawer.
  function handleCreate(creation: EntityCreation, { kind, entity }: OrganizationNode) {
    switch (creation) {
      case 'partner':
        setIsCreatePartnerOpen(true);
        break;
      case 'account':
        if (kind === 'Partner') {
          openCreateAccountUnderPartner(entity);
        } else {
          openCreateAccount();
        }

        break;
      case 'subAccount':
        openCreateSubAccount(entity);
        break;
    }
  }

  return (
    <>
      <div className={cn('panel', isInset && 'inset')}>
        <OrganizationChartControls
          displayLevel={displayLevel}
          onDisplayLevelChange={setDisplayLevel}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
        >
          {filters}
        </OrganizationChartControls>

        {/* Figma `Chart Area`: a dotted canvas the tree pans on. */}
        <div className={cn('canvas')} data-testid="organizationChart" aria-busy={isLoading}>
          {isLoading ? (
            <div className={cn('canvasSkeleton')}>
              <Skeleton width={220} height={140} />
            </div>
          ) : (
            organization && (
              <OrganizationChart
                organization={organization}
                show={show}
                filter={filter}
                displayLevel={displayLevel}
                pageSize={pageSize}
                onAccountClick={openAccount}
                onPartnerClick={canOpenPartnerDetails ? openPartner : undefined}
                creationPermissions={creationPermissions}
                onCreate={handleCreate}
                onTrackProgress={
                  canTrackProgress ? (entity) => setTrackedEntity(toOnboardingEntity(entity)) : undefined
                }
              />
            )
          )}
        </div>
      </div>

      {isCreatePartnerOpen && (
        <PartnerCreationDrawer onClose={() => setIsCreatePartnerOpen(false)} onCreated={onOrganizationChanged} />
      )}

      {isCreateAccountOpen && (
        <PartnerAccountCreation onClose={() => setIsCreateAccountOpen(false)} onCreated={onOrganizationChanged} />
      )}

      {creationPartner && (
        <PartnerAccountCreation
          partnerAccount={creationPartner}
          onClose={() => setCreationPartner(null)}
          onCreated={onOrganizationChanged}
        />
      )}

      {creationParent && (
        <PartnerAccountCreation
          parentAccount={creationParent}
          onClose={() => setCreationParent(null)}
          onSubAccountCreated={onOrganizationChanged}
        />
      )}

      <AccountDetailsDrawer
        account={selectedAccount}
        isFetching={isFetchingAccount}
        onClose={closeAccount}
        onViewFullDetails={(account) =>
          history.push(accountPageRoute.getPath({ accountID: account.AccountID, currency: account.Currency }))
        }
      />

      <PartnerDetailsDrawer
        partner={selectedPartner}
        isLoadingPartner={isFetchingPartner}
        onClose={closePartner}
        onViewFullDetails={
          canCreatePartner
            ? (partner) => history.push(partnerPageRoute.getPath({ partnerAccountID: partner.PartnerAccountID }))
            : undefined
        }
      />

      <ApplicationDetailsDrawer
        entity={trackedEntity}
        onClose={() => setTrackedEntity(null)}
        onChanged={onOrganizationChanged}
        isReadOnly={!isRoleAdmin}
      />
    </>
  );
}
